import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Checkbox, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import paymentportalservice from "../../../services/paymentportalservice";

// Who — besides super admins and the creator — should see the campaign being
// created. The campaign has no id yet, so nothing is saved from here: the
// form submits the picked ids to POST /campaign/share once the campaign
// exists. Access can be changed later from the Share icon on the campaign
// card.
const ShareWithDropdown = ({ value = [], onChange, disabled = false }) => {
  const [users, setUsers] = useState([]);
  // Super admins — always visible regardless of sharing (common/useBaseCondition.js on the
  // backend) — so the field reads as "add people beyond who already sees this", not as if
  // nobody has access yet.
  const [alwaysVisible, setAlwaysVisible] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    paymentportalservice
      .FetchShareableUsers()
      .then((data) => {
        if (cancelled) return;
        setUsers(Array.isArray(data?.users) ? data.users : []);
        setAlwaysVisible(Array.isArray(data?.alwaysVisible) ? data.alwaysVisible : []);
        setFetchError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setUsers([]);
        setAlwaysVisible([]);
        setFetchError(error?.response?.data || error.message || "Failed to fetch users");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // "and you" always applies — only a super admin can reach this form (isSuperUser gates
  // both the Add Campaign flow and this field's visibility in CampaignForm.jsx), and the
  // backend leaves the requester out of alwaysVisible for exactly this reason.
  const alwaysVisibleText = useMemo(() => {
    const names = alwaysVisible.map((user) => user.fullName || "Unnamed user");
    return [...names, "you"].join(", ");
  }, [alwaysVisible]);

  const options = useMemo(
    () =>
      users.map((user) => ({
        value: String(user._id),
        label: user.fullName || "Unnamed user",
        caption: [user.email, user.role].filter(Boolean).join(" · "),
      })),
    [users]
  );

  // The form owns the selection as a plain array of ids, so it survives the
  // reset() on submit like every other field — this maps back to the option
  // objects Autocomplete needs to render its chips.
  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  );

  return (
    <Box sx={{ width: 350 }}>
      {!loading && !fetchError && (
        <Typography sx={{ fontSize: 12, opacity: 0.7, mb: 0.5 }}>
          Already visible to: {alwaysVisibleText}
        </Typography>
      )}
      <Autocomplete
        multiple
        disableCloseOnSelect
        id="share-with-select"
        options={options}
        loading={loading}
        disabled={disabled || Boolean(fetchError)}
        value={selectedOptions}
        onChange={(event, newValue) => onChange(newValue.map((option) => option.value))}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        noOptionsText="No users available"
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value}>
            <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 14, fontWeight: 600 }}>{option.label}</Box>
              {option.caption && (
                <Box sx={{ fontSize: 12, opacity: 0.7 }}>{option.caption}</Box>
              )}
            </Box>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              size="small"
              label={option.label}
              {...getTagProps({ index })}
              key={option.value}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Share With"
            error={Boolean(fetchError)}
            helperText={
              fetchError || "Optional — these users will also see this campaign"
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default ShareWithDropdown;
